/**
 * Public Marketplace Types - Story 7.1: Create Marketplace Homepage
 * Type definitions for public-facing marketplace features
 */

import type { ProductCategory, ProductImage, ProductStatus } from './products.types';

/**
 * Public Product Type
 * Product information displayed on public marketplace
 * Contains only public-facing fields (no seller-specific data)
 */
export interface PublicProduct {
  id: string;
  name: string;
  description: string;
  category: ProductCategory;
  price?: number;
  unit: string;
  images: ProductImage[];
  location: {
    state: string;
    district: string;
  };
  createdAt: string;
  status: ProductStatus;
  minimumOrderQuantity?: number;
}

/**
 * Featured Products Query Params
 * Parameters for fetching featured products
 */
export interface FeaturedProductsParams {
  page: number;
  size: number;
  sort: string; // e.g., "createdAt,desc"
}

/**
 * Paginated Public Products Response
 * API response structure for public product listings
 */
export interface PaginatedPublicProductsResponse {
  data: PublicProduct[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Product Card Props
 * Props for the ProductCard component
 */
export interface ProductCardProps {
  product: PublicProduct;
  onProductClick?: (productId: string) => void;
  className?: string;
}

/**
 * Product Skeleton Props
 * Props for the ProductSkeleton component
 */
export interface ProductSkeletonProps {
  className?: string;
}

/**
 * Featured Products Props
 * Props for the FeaturedProducts component
 */
export interface FeaturedProductsProps {
  /** Optional callback when a product is clicked */
  onProductClick?: (productId: string) => void;
  /** Optional callback when products finish loading */
  onLoadComplete?: () => void;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Category Badge Config
 * Configuration for category badge styling
 */
export interface CategoryBadgeConfig {
  color: string;
  bgColor: string;
  label: string;
}

/**
 * Category Badge Map
 * Maps product categories to their badge configurations
 */
export const CATEGORY_BADGE_MAP: Record<ProductCategory, CategoryBadgeConfig> = {
  BIOGAS: {
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    label: 'Biogas',
  },
  BIOMASS: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Biomass',
  },
  SEEDS: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    label: 'Seeds',
  },
  EQUIPMENT: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    label: 'Equipment',
  },
  FERTILIZER: {
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    label: 'Fertilizer',
  },
  OTHER: {
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    label: 'Other',
  },
};

/**
 * Product Error State
 * Error information for failed product operations
 */
export interface ProductError {
  message: string;
  statusCode?: number;
  errorCode?: string;
}

/**
 * Product Query Result
 * Result type for React Query hooks
 */
export interface ProductQueryResult {
  products: PublicProduct[];
  isLoading: boolean;
  isError: boolean;
  error: ProductError | null;
  refetch: () => Promise<void>;
}

/**
 * Product Browse Filters - Story 7.2: Create Product Browse/Listing Page
 * Filter options for product browsing
 */
export interface ProductBrowseFilters {
  search?: string;
  category?: ProductCategory | null;
  state?: string | null;
  district?: string | null;
  sortBy?: 'newest' | 'price_low' | 'price_high';
  page: number;
  pageSize: number;
}

/**
 * Location Filter Options
 * State and district options for location filtering
 */
export interface LocationOption {
  value: string;
  label: string;
}

/**
 * State District Mapping
 * Maps states to their districts
 */
export interface StateDistrictMap {
  [state: string]: string[];
}

/**
 * Product Browse Page Props
 * Props for ProductBrowsePage component
 */
export interface ProductBrowsePageProps {
  className?: string;
  onProductClick?: (productId: string) => void;
}

/**
 * Product Grid Props
 * Props for ProductGrid component
 */
export interface ProductGridProps {
  products: PublicProduct[];
  isLoading: boolean;
  isError: boolean;
  error?: ProductError | null;
  onProductClick?: (productId: string) => void;
  onRetry?: () => void;
  className?: string;
}

/**
 * Filter Sidebar Props
 * Props for FilterSidebar component
 */
export interface FilterSidebarProps {
  filters: ProductBrowseFilters;
  onFiltersChange: (filters: Partial<ProductBrowseFilters>) => void;
  states: LocationOption[];
  districts: LocationOption[];
  isLoading?: boolean;
  className?: string;
}

/**
 * Sort Dropdown Props
 * Props for SortDropdown component
 */
export interface SortDropdownProps {
  value: 'newest' | 'price_low' | 'price_high';
  onChange: (sortBy: 'newest' | 'price_low' | 'price_high') => void;
  className?: string;
}

/**
 * Product Detail Types - Story 7.3: Create Product Detail Page
 */

/**
 * Detailed Product with Seller Information
 * Extended product type for product detail page
 */
export interface ProductDetailData extends PublicProduct {
  seller: {
    id: string;
    name: string;
    district: string;
    state: string;
  };
  inquiryCount?: number;
}

/**
 * Product Image Gallery Props
 * Props for ProductImageGallery component
 */
export interface ProductImageGalleryProps {
  images: ProductImage[];
  productName: string;
  className?: string;
}

/**
 * Product Info Props
 * Props for ProductInfo component
 */
export interface ProductInfoProps {
  product: ProductDetailData;
  className?: string;
}

/**
 * Seller Info Props
 * Props for SellerInfo component
 */
export interface SellerInfoProps {
  seller: {
    id: string;
    name: string;
    district: string;
    state: string;
  };
  className?: string;
}

/**
 * Inquiry Form Props
 * Props for InquiryForm component
 */
export interface InquiryFormProps {
  productId: string;
  productName: string;
  minimumOrderQuantity?: number;
  unit?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

/**
 * Product Detail Page Props
 * Props for ProductDetailPage component
 */
export interface ProductDetailPageProps {
  className?: string;
}
